import { Grid, useMediaQuery } from "@mui/material";
import { Create, Delete } from "@mui/icons-material";

import { SelectInput } from "@dzambalaorg/mui-inputs-core";
import { SearchWithFilters } from "@dzambalaorg/mui-form-search";
import { Breadcrumbs, PageHeader, ProgressOverlay } from "@dzambalaorg/mui-page-layout";
import { DeleteDialog } from "@dzambalaorg/mui-dialog-delete";
import { ListAction, ListActions, StyledListItem, StyledListWrapper, StyledPagination } from "@framework/styled";
import { IUser, UserRole, UserStatus } from "@framework/types";
import { useDialogDelete, useFetchQuery } from "@dzambalaorg/react-hooks";
import { IPaginationResult } from "@dzambalaorg/types-collection";
import { useGlobalDialogFormCallbacks } from "@framework/shared";
import { FormGrid, List } from "@dzambalaorg/ui-kit";

import { userFormValidationSchema } from "./form/validation";
import { OfficeDialogKeys } from "../../../shared";

const USERS_URL = "/users";

export const User = () => {
  const isMd = useMediaQuery(theme => theme.breakpoints.down("md"));

  const { isLoading, take, skip, onChangePage, hasNextPage, loadMoreRef, allRows, totalCount, refetch } = useFetchQuery<
    IPaginationResult<IUser>
  >(USERS_URL, { enableInfiniteScroll: isMd }, void 0, {
    userStatus: [UserStatus.ACTIVE],
    userRoles: [],
  });

  const { openDialog } = useGlobalDialogFormCallbacks<IUser, IUser>();
  const { handleDelete } = useDialogDelete<IUser>();

  const userEdit = (user: IUser) => () => {
    openDialog(OfficeDialogKeys.USERS, user, {
      refetch,
      initialValues: user,
      message: "dialogs.edit",
      testId: "UserFormDialog",
      url: `${USERS_URL}/${user.id}`,
      validationSchema: userFormValidationSchema,
      filter: ({ id: _id, ...rest }) => rest,
      prepareInitialValues: ({ id, email, displayName, language, imageUrl, userRoles, userStatus, comment }) => ({
        id,
        email,
        displayName,
        language,
        imageUrl,
        userRoles,
        userStatus,
        comment,
      }),
      componentProps: {
        createdAt: user.createdAt,
      },
    });
  };

  return (
    <Grid>
      <Breadcrumbs path={["dashboard", "users"]} />

      <PageHeader message="pages.users.title" />

      <SearchWithFilters initialValues={{ userStatus: [UserStatus.ACTIVE], userRoles: [] }}>
        <FormGrid columns={[2]}>
          <SelectInput multiple name="userStatus" options={UserStatus} />
          <SelectInput multiple name="userRoles" options={UserRole} />
        </FormGrid>
      </SearchWithFilters>

      <ProgressOverlay isLoading={isLoading}>
        <StyledListWrapper count={allRows.length} isLoading={isLoading}>
          {allRows.map(user => (
            <StyledListItem key={user.id}>
              <List.ItemText>{user.displayName}</List.ItemText>
              <ListActions>
                <ListAction
                  onClick={userEdit(user)}
                  message="form.buttons.edit"
                  dataTestId="UserEditButton"
                  icon={Create}
                />
                <ListAction
                  onClick={handleDelete(user)}
                  message="form.buttons.delete"
                  dataTestId="UserDeleteButton"
                  icon={Delete}
                />
              </ListActions>
            </StyledListItem>
          ))}
        </StyledListWrapper>
      </ProgressOverlay>

      {!isMd && totalCount > take && (
        <StyledPagination
          shape="rounded"
          page={skip / take + 1}
          count={Math.ceil(totalCount / take)}
          onChange={onChangePage}
        />
      )}
      {isMd && hasNextPage && <div ref={loadMoreRef} style={{ height: "20px", margin: "20px 0" }} />}

      <DeleteDialog url={USERS_URL} refetch={refetch} />
    </Grid>
  );
};
